import React, { useState } from 'react';
import { FiTrendingUp, FiPlus } from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { useApi } from '../hooks/useApi';
import { useAuthStore } from '../stores';
import { useModalStore } from '../stores';
import SignalCard from '../components/Community/SignalCard';
import CreateSignalModal from '../components/Community/CreateSignalModal';
import { Loader } from '../components/Common/Loader';
import { ErrorDisplay } from '../components/Common/ErrorDisplay';

const SignalsPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { openModal } = useModalStore();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<'all' | 'signal' | 'post'>('all');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('community');

  const limit = 20;
  const { data, isLoading, error } = useApi.useSignals(
    page,
    limit,
    typeFilter === 'all' ? undefined : typeFilter
  );

  const signals = data?.signals || [];
  const pagination = data?.pagination;

  // Listen for signal events via WebSocket
  React.useEffect(() => {
    if (!user) return;

    import('../services/socketService').then(({ socketService }) => {
      const socket = socketService.getSocket();
      if (!socket) return;

      // Handle new signals
      const handleNewSignal = () => {
        console.log('📢 SignalsPage: New signal received from another session');
        // Cache is already updated by socketService, no need to refetch
      };

      // Handle deleted signals - remove from cache immediately
      const handleSignalDeleted = (data: { signalId: string }) => {
        console.log('🗑️ SignalsPage: Signal deleted:', data.signalId);
        const queryCache = queryClient.getQueryCache();
        const allSignalQueries = queryCache.findAll({ queryKey: ['signals'] });
        
        allSignalQueries.forEach((query) => {
          const oldData = query.state.data as any;
          if (oldData && oldData.signals) {
            const updatedSignals = oldData.signals.filter((s: any) => s._id !== data.signalId);
            queryClient.setQueryData(query.queryKey, {
              ...oldData,
              signals: updatedSignals,
              pagination: {
                ...oldData.pagination,
                total: Math.max(0, (oldData.pagination?.total || 0) - 1)
              }
            });
          }
        });
        
        // Also update master signals queries
        const masterQueries = queryCache.findAll({ queryKey: ['signals', 'master'] });
        masterQueries.forEach((query) => {
          const oldData = query.state.data as any;
          if (oldData && oldData.signals) {
            const updatedSignals = oldData.signals.filter((s: any) => s._id !== data.signalId);
            queryClient.setQueryData(query.queryKey, {
              ...oldData,
              signals: updatedSignals,
              pagination: {
                ...oldData.pagination,
                total: Math.max(0, (oldData.pagination?.total || 0) - 1)
              }
            });
          }
        });
      };

      socket.on('signal:new', handleNewSignal);
      socket.on('signal:deleted', handleSignalDeleted);

      return () => {
        socket.off('signal:new', handleNewSignal);
        socket.off('signal:deleted', handleSignalDeleted);
      };
    });
  }, [user, queryClient]);

  const isMaster = user?.role === 'master' || user?.role === 'admin';

  if (!user) {
    return null;
  }

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header
          user={user}
          onLogout={handleLogout}
          showSidebarToggle
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader message="Loading signals..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header
          user={user}
          onLogout={handleLogout}
          showSidebarToggle
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <ErrorDisplay error="Failed to load signals" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header
        user={user}
        onLogout={handleLogout}
        showSidebarToggle
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
      />

      <div className="flex relative w-full max-w-full pt-0 lg:pt-0">
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          user={user}
          isOpen={isSidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="flex-1 min-w-0 max-w-full px-3 py-4 sm:px-6 lg:px-6 lg:ml-72 overflow-x-hidden">
          <div className="max-w-4xl mx-auto min-w-0">
        {/* Header */}
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-gradient-primary p-3 rounded-xl shrink-0">
              <FiTrendingUp className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Signals & Posts
                {pagination && pagination.total > 0 && (
                  <span className="ml-2 text-lg text-primary-500 dark:text-primary-400">
                    ({pagination.total})
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Community trading insights</p>
            </div>
          </div>

          {isMaster && (
            <button
              onClick={() => openModal('create-signal')}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors"
            >
              <FiPlus className="h-5 w-5" />
              Create Post
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setTypeFilter('all')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              typeFilter === 'all'
                ? 'bg-primary-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setTypeFilter('signal')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              typeFilter === 'signal'
                ? 'bg-primary-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
            }`}
          >
            Signals
          </button>
          <button
            onClick={() => setTypeFilter('post')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              typeFilter === 'post'
                ? 'bg-primary-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
            }`}
          >
            Posts
          </button>
        </div>

        {/* Signals Feed */}
        {signals.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-12 text-center">
            <FiTrendingUp className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">No signals or posts yet</p>
            {isMaster && (
              <button
                onClick={() => openModal('create-signal')}
                className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors"
              >
                Create First Post
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {signals.map((signal) => (
                <SignalCard key={signal._id} signal={signal} />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
          </div>
        </main>
      </div>
      {isMaster && <CreateSignalModal />}
    </div>
  );
};

export default SignalsPage;

